/**
 * Shared registry that maps post-like payloads to their full and compact renderers.
 * Mixed entity surfaces can delegate here instead of repeating per-kind switches.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import ActionCardRefactored from "components/Actions/ActionCardRefactored";
import EntityCompactCard from "components/Entities/EntityCompactCard";
import FeedbackCardRefactored from "components/Feedback/FeedbackCardRefactored";
import ImpactCardRefactored from "components/Impacts/ImpactCardRefactored";
import SolutionCardRefactored from "components/Solutions/SolutionCardRefactored";
import TeabateCard from "components/Teabates/TeabateCard";
import { getPostContextDefaults } from "components/Posts/display/postDisplayConfig";
import { resolvePostKind } from "components/Posts/display/resolvePostKind";
import type {
  PostDisplayContext,
  PostDisplayEntity,
  SharedPostCardProps,
} from "components/Posts/display/postDisplayTypes";
import { getPostKindProfilePath } from "util/entityMetadata";

interface PostRendererBaseProps extends SharedPostCardProps {
  entityType: string;
  entity: PostDisplayEntity;
}

export interface PostFullRendererProps extends PostRendererBaseProps {
  context?: Exclude<PostDisplayContext, "compact">;
}

export interface PostCompactRendererProps extends PostRendererBaseProps {
  showVote?: boolean;
  onClick?: () => void;
}

function resolveSharedProps(
  context: PostDisplayContext,
  overrides: SharedPostCardProps
): Required<SharedPostCardProps> {
  const defaults = getPostContextDefaults(context);

  return {
    showMeta: overrides.showMeta ?? defaults.showMeta,
    showActions: overrides.showActions ?? defaults.showActions,
    truncateDescription: overrides.truncateDescription ?? defaults.truncateDescription,
    isEntitySection: overrides.isEntitySection ?? defaults.isEntitySection,
    isEntityProfilePage: overrides.isEntityProfilePage ?? defaults.isEntityProfilePage,
  };
}

const PostFullRenderer: React.FC<PostFullRendererProps> = ({
  context = "feed",
  entityType,
  entity,
  ...overrides
}) => {
  const sharedProps = resolveSharedProps(context, overrides);

  switch (entityType) {
    case "solution":
      return <SolutionCardRefactored solution={entity as React.ComponentProps<typeof SolutionCardRefactored>["solution"]} {...sharedProps} />;
    case "feedback":
      return <FeedbackCardRefactored feedback={entity as React.ComponentProps<typeof FeedbackCardRefactored>["feedback"]} {...sharedProps} />;
    case "action":
      return (
        <ActionCardRefactored
          action={entity as React.ComponentProps<typeof ActionCardRefactored>["action"]}
          isActionProfilePage={sharedProps.isEntityProfilePage}
          isEntitySection={sharedProps.isEntitySection}
          showMeta={sharedProps.showMeta}
          showActions={sharedProps.showActions}
          truncateDescription={sharedProps.truncateDescription}
        />
      );
    case "impact":
    case "teabate":
      return resolvePostKind(entityType, entity) === "teabate" ? (
        <TeabateCard teabate={entity as React.ComponentProps<typeof TeabateCard>["teabate"]} {...sharedProps} />
      ) : (
        <ImpactCardRefactored impact={entity as React.ComponentProps<typeof ImpactCardRefactored>["impact"]} {...sharedProps} />
      );
    default:
      return null;
  }
};

const PostCompactRenderer: React.FC<PostCompactRendererProps> = ({
  entityType,
  entity,
  showVote = true,
  showMeta,
  showActions,
  onClick,
}) => {
  const navigate = useNavigate();
  const resolvedKind = resolvePostKind(entityType, entity);
  if (!resolvedKind) {
    return null;
  }

  const sharedProps = resolveSharedProps("compact", { showMeta, showActions });

  return (
    <EntityCompactCard
      entity={entity}
      displayEntity={entity}
      entityId={entity.id}
      entityType={resolvedKind}
      showMeta={sharedProps.showMeta}
      showActions={sharedProps.showActions}
      compactCard={true}
      showVote={showVote}
      onClick={onClick ?? (() => navigate(getPostKindProfilePath(resolvedKind, entity.id)))}
    />
  );
};

export { PostFullRenderer, PostCompactRenderer };
